import { get } from 'lodash'
import { Middleware } from 'redux'

import { FETCH_TENANT_CONFIGURATION } from '@/components/App/actionTypes'
import { expiredSessionFailure } from '@/components/AuthenticationHandler/actions'
import { REQUEST } from '@/store/actionTypes'
import fetch, { replacePlaceholders } from '@/utilities/fetch'

import { RootState } from '..'

const requestControllers: Record<string, AbortController[]> = {}

/**
 * Each action's abort controllers are a queue so we can keep track of which requests are inflight for a given action.
 * This allows us to cancel inflight requests in favor of a more recent request being dispatched. Otherwise multiple
 * identical requests can be inflight at the same moment and completion order is not guaranteed.
 *
 * @param actionType - Action type name without trailing underscore to be prefixed to `REQUEST`, `SUCCESS` and `ERROR` dispatched actions.
 * @return Instance of an `AbortController`.
 */
const createAbortController = (actionType: string) => {
  const signal = new AbortController()

  if (!requestControllers[actionType]) {
    requestControllers[actionType] = []
  }

  requestControllers[actionType].push(signal)

  return signal
}

/**
 * Remove the abort controller for a completed request. Request completion order isn't guaranteed so we can't naively
 * remove the first item from the abort controller queue. Instead we use object reference equality to determine which
 * queued abort controller belongs to the completed request and remove that one.
 *
 * @param actionType - Action type name without trailing underscore to be prefixed to `REQUEST`, `SUCCESS` and `ERROR` dispatched actions.
 * @param controller - Instance of an `AbortController`.
 */
const removeAbortController = (actionType: string, controller: any) => {
  requestControllers[actionType] = requestControllers[actionType].filter(
    actionController => actionController !== controller
  )
}

/**
 * Execute all queued abort controllers for a given action thereby cancelling all of it's inflight requests.
 *
 * @param {string} actionType - Action type name without trailing underscore to be prefixed to `REQUEST`, `SUCCESS` and `ERROR` dispatched actions.
 */
const executeAbortControllers = (actionType: string) => {
  requestControllers[actionType].forEach(controller => {
    try {
      controller.abort()
    } catch {
      // swallow a failed aborted request as our fallback is letting the request complete as normal
    }
  })

  requestControllers[actionType] = []
}

/**
 * Redux middleware that initiates an API request using the details of the action. A `REQUEST` action is dispatched
 * before all API requests start. Either a `SUCCESS` or `ERROR` action is then dispatched depending on the outcome. API
 * requests can also conditionally be made depending if the response is already held in cache. In such cases, a `CACHED`
 * action is dispatched to appropriately update the request tracking mechanism without triggering other reducers.
 */
const requestMiddleware: Middleware<{}, RootState> = store => next => {
  /**
   * @param {object} action - A dispatched Redux action.
   * @param {string} action.type - Must be `REQUEST` for this middleware to apply.
   * @param {string} action.actionType - Action type name without trailing underscore to be prefixed to `REQUEST`, `SUCCESS` and `ERROR` dispatched actions.
   * @param {object} [action.options] - Map of configuration options to use for the API request.
   * @param {object} [action.options.args] - Map of arguments to include in dispatched actions. Placeholders `:organizationId` and `:spaceId` can be used.
   * @param {boolean} [action.options.cached] - If the response for this request is already cached and does not need to be re-fetched.
   * @param {boolean} [action.options.ignoreLatest=false] - If a request is already pending for this action type, ignore this request.
   * @param {boolean} [action.options.ignoreLeading=false] - If a request is already pending for this action type, cancel the pending request.
   * @param {boolean} [action.options.promisify=false] - If a promise should be returned for chaining in addition to dispatching lifecycle events.
   * @param {object} [action.params] - Map of parameters to pass to fetch for the API request.
   * @param {string} action.url - URL of the API request to fetch. Placeholders `:organizationId` and `:spaceId` can be used.
   * @param {object} action.additionalActionParams - Additonal params to include in the action dispatch which will be proxied to reducer but not used in the request
   * @return {Promise}
   */
  return async action => {
    if (action.type !== REQUEST) {
      return next(action)
    }

    const { actionType, url, additionalActionParams } = action
    const options = get(action, 'options', {})
    const {
      args = {},
      cached = false,
      ignoreLatest = false,
      ignoreLeading = false,
      promisify = false,
      ...rest
    } = options
    const params = get(action, 'params', {})
    const actionArgs = replacePlaceholders(args)
    const hasRequestPending = get(requestControllers, actionType + '_' + url, []).length > 0

    if (ignoreLeading && hasRequestPending) {
      executeAbortControllers(actionType + '_' + url)
    } else if (cached || (ignoreLatest && hasRequestPending)) {
      // client has opted to use a cached response or use an inflight request for this action rather than spawning another
      return
    }

    // prevent certain properties from being overridden by `actionArgs`
    delete actionArgs.error
    delete actionArgs.response
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const controller = createAbortController(actionType + '_' + url)
        params.signal = controller.signal

        store.dispatch({
          ...actionArgs,
          shouldNotClearError: get(options, 'shouldNotClearError'),
          type: `${actionType}_REQUEST`
        })
        const response = await fetch(url, params, rest)

        // I'm sorry... I needed to find a way to "inject" an additional lookup in the tenant configuration call
        // without needing to refactor a bunch of other code right now :( - Hopefully this will be replaced soon.
        if (actionType === FETCH_TENANT_CONFIGURATION) {
          response.openidConfiguration = await globalThis
            .fetch(`${response.issuer.replace(/\/$/, '')}/.well-known/openid-configuration`)
            .then(res => res.json())
        }

        store.dispatch({
          ...actionArgs,
          shouldNotClearError: get(options, 'shouldNotClearError'),
          type: `${actionType}_SUCCESS`,
          response,
          additionalActionParams
        })

        removeAbortController(actionType + '_' + url, controller)

        return resolve(response)
      } catch (error) {
        // when an inflight request is canceled an AbortError is thrown that we want to swallow
        if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
          // eslint-disable-next-line unicorn/no-useless-undefined
          return resolve(undefined)
        }

        if (error && typeof error === 'object' && 'code' in error && error.code === 401) {
          expiredSessionFailure()(store.dispatch, store.getState, null)
        }

        store.dispatch({ ...actionArgs, url, type: `${actionType}_ERROR`, error: error })

        // to maintain backwards compatibility we can't reject for action invocations that aren't expecting promises
        // eslint-disable-next-line unicorn/no-useless-undefined
        return promisify ? reject(error) : resolve(undefined)
      }
    })
  }
}

export default requestMiddleware
